import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';

interface Auftrag {
  id: string;
  auftragsnummer: string;
  internal_number: string;
  kostenstelle?: string;
  sap_nummer?: string;
  auftragsbezeichnung: string;
  auftragsdatum: string;
  monteur_id: string;
  kunde_id: string;
  standort_id: string;
  status: 'neu' | 'begonnen' | 'abgeschlossen' | 'abgerechnet';
  pdf_name?: string;
  pdf_data?: string;
}

interface Monteur {
  id: string;
  name: string;
}

interface Kunde {
  id: string;
  name: string;
  type: 'ohne_kostenstelle' | 'sap_nummer' | 'kostenstelle';
  types?: string[];
}

interface Standort {
  id: string;
  name: string;
  adresse: string;
  strasse: string;
  plz: string;
  stadt: string;
  kunde_id: string;
  standortnummer: string | null;
}

function AuftragsListe() {
  const [auftraege, setAuftraege] = useState<Auftrag[]>([]);
  const [monteure, setMonteure] = useState<Monteur[]>([]);
  const [kunden, setKunden] = useState<Kunde[]>([]);
  const [standorte, setStandorte] = useState<Standort[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nextInternalNumber, setNextInternalNumber] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<Omit<Auftrag, 'id' | 'status'>>({
    auftragsnummer: '',
    auftragsbezeichnung: '',
    auftragsdatum: new Date().toISOString().split('T')[0],
    monteur_id: '',
    kunde_id: '',
    standort_id: '',
  });

  useEffect(() => {
    loadData();
    fetchNextInternalNumber();
  }, []);

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      standort_id: ''
    }));
  }, [formData.kunde_id]);

  async function fetchNextInternalNumber() {
    try {
      const { data, error } = await supabase.rpc('generate_internal_number');
      
      if (error) throw error;
      
      setNextInternalNumber(data);
    } catch (error) {
      console.error('Fehler beim Laden der nächsten internen Nummer:', error);
    }
  }

  async function loadData() {
    try {
      setLoading(true);
      const [monteureRes, kundenRes, standorteRes, auftraegeRes] = await Promise.all([
        supabase.from('monteure').select('*'),
        supabase.from('kunden').select('*'),
        supabase.from('standorte').select('*'),
        supabase.from('auftraege').select('*'),
      ]);

      if (monteureRes.error) throw monteureRes.error;
      if (kundenRes.error) throw kundenRes.error;
      if (standorteRes.error) throw standorteRes.error;
      if (auftraegeRes.error) throw auftraegeRes.error;

      setMonteure(monteureRes.data || []);
      setKunden(kundenRes.data || []);
      setStandorte(standorteRes.data || []);
      setAuftraege(auftraegeRes.data || []);
    } catch (error) {
      console.error('Fehler beim Laden der Daten:', error);
      alert('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          setFormData((prev) => ({
            ...prev,
            pdf_name: file.name,
            pdf_data: event.target.result as string,
          }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const checkAuftragsnummerExists = async (auftragsnummer: string, excludeId?: string): Promise<boolean> => {
    try {
      let query = supabase
        .from('auftraege')
        .select('id')
        .eq('auftragsnummer', auftragsnummer);
      
      if (excludeId) {
        query = query.neq('id', excludeId);
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error('Fehler beim Prüfen der Auftragsnummer:', error);
        return false;
      }
      
      return data && data.length > 0;
    } catch (error) {
      console.error('Unerwarteter Fehler beim Prüfen der Auftragsnummer:', error);
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    try {
      if (editingId) {
        const exists = await checkAuftragsnummerExists(formData.auftragsnummer, editingId);
        if (exists) {
          setError(`Die Auftragsnummer "${formData.auftragsnummer}" existiert bereits. Bitte wählen Sie eine andere Nummer.`);
          return;
        }
        
        const { data, error } = await supabase
          .from('auftraege')
          .update(formData)
          .eq('id', editingId)
          .select()
          .single();

        if (error) throw error;

        setAuftraege(prev => prev.map(auftrag => 
          auftrag.id === editingId ? data : auftrag
        ));
        setEditingId(null);
      } else {
        const exists = await checkAuftragsnummerExists(formData.auftragsnummer);
        if (exists) {
          setError(`Die Auftragsnummer "${formData.auftragsnummer}" existiert bereits. Bitte wählen Sie eine andere Nummer.`);
          return;
        }
        
        const { data, error } = await supabase
          .from('auftraege')
          .insert([{ ...formData, status: 'neu' }])
          .select()
          .single();

        if (error) throw error;

        setAuftraege((prev) => [...prev, data]);
        
        // Fetch next internal number after successful insert
        fetchNextInternalNumber();
      }
      
      // Reset form
      setFormData({
        auftragsnummer: '',
        auftragsbezeichnung: '',
        auftragsdatum: new Date().toISOString().split('T')[0],
        monteur_id: '',
        kunde_id: '',
        standort_id: '',
      });
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      setError('Fehler beim Speichern des Auftrags');
    }
  };

  const startEditing = (auftrag: Auftrag) => {
    setEditingId(auftrag.id);
    setFormData({
      auftragsnummer: auftrag.auftragsnummer,
      auftragsbezeichnung: auftrag.auftragsbezeichnung,
      auftragsdatum: auftrag.auftragsdatum,
      monteur_id: auftrag.monteur_id,
      kunde_id: auftrag.kunde_id,
      standort_id: auftrag.standort_id,
      kostenstelle: auftrag.kostenstelle,
      sap_nummer: auftrag.sap_nummer,
      pdf_name: auftrag.pdf_name,
      pdf_data: auftrag.pdf_data,
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setFormData({
      auftragsnummer: '',
      auftragsbezeichnung: '',
      auftragsdatum: new Date().toISOString().split('T')[0],
      monteur_id: '',
      kunde_id: '',
      standort_id: '',
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const deleteAuftrag = async (id: string) => {
    if (!confirm('Sind Sie sicher, dass Sie diesen Auftrag löschen möchten?')) {
      return;
    }
    
    try {
      const { error } = await supabase
        .from('auftraege')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setAuftraege(prev => prev.filter(auftrag => auftrag.id !== id));
      
      if (editingId === id) {
        cancelEditing();
      }
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      alert('Fehler beim Löschen des Auftrags');
    }
  };

  const getStatusLabel = (status: Auftrag['status']) => {
    switch (status) {
      case 'neu':
        return 'Neu';
      case 'begonnen':
        return 'In Bearbeitung';
      case 'abgeschlossen':
        return 'Abgeschlossen';
      case 'abgerechnet':
        return 'Abgerechnet';
      default:
        return status;
    }
  };

  const shouldShowKostenstelle = (kundeId: string) => {
    const kunde = kunden.find(k => k.id === kundeId);
    if (!kunde) return false;
    
    return kunde.types ? 
      kunde.types.includes('kostenstelle') : 
      kunde.type === 'kostenstelle';
  };

  const shouldShowSapNummer = (kundeId: string) => {
    const kunde = kunden.find(k => k.id === kundeId);
    if (!kunde) return false;
    
    return kunde.types ? 
      kunde.types.includes('sap_nummer') : 
      kunde.type === 'sap_nummer';
  };

  const filteredStandorte = formData.kunde_id 
    ? standorte.filter(standort => standort.kunde_id === formData.kunde_id)
    : [];

  if (loading) {
    return <div className="text-center py-8">Laden...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold mb-6 text-gray-800">Auftragsverwaltung</h1>
        
        {error && (
          <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-md border border-red-200">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {!editingId && nextInternalNumber && (
            <div className="md:col-span-2 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-800">
                <strong>Nächste Bestellnummer:</strong> {nextInternalNumber}
              </p>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Auftragsnummer</label>
            <input
              type="text"
              name="auftragsnummer"
              value={formData.auftragsnummer}
              onChange={handleInputChange}
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="z.B. A-2024-001"
            />
            <p className="mt-1 text-xs text-gray-500">
              Bitte geben Sie die Auftragsnummer aus dem zugesendeten Auftrag ein
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Kunde</label>
            <select
              name="kunde_id"
              value={formData.kunde_id}
              onChange={handleInputChange}
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">Bitte wählen...</option>
              {kunden.map((kunde) => (
                <option key={kunde.id} value={kunde.id}>
                  {kunde.name}
                </option>
              ))}
            </select>
          </div>

          {shouldShowKostenstelle(formData.kunde_id) && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Kostenstelle</label>
              <input
                type="text"
                name="kostenstelle"
                value={formData.kostenstelle || ''}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="z.B. KS-100"
              />
            </div>
          )}

          {shouldShowSapNummer(formData.kunde_id) && (
            <div>
              <label className="block text-sm font-medium text-gray-700">SAP Nummer</label>
              <input
                type="text"
                name="sap_nummer"
                value={formData.sap_nummer || ''}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="z.B. SAP-2024-001"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700">Monteur</label>
            <select
              name="monteur_id"
              value={formData.monteur_id}
              onChange={handleInputChange}
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">Bitte wählen...</option>
              {monteure.map((monteur) => (
                <option key={monteur.id} value={monteur.id}>
                  {monteur.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Standort</label>
            <select
              name="standort_id"
              value={formData.standort_id}
              onChange={handleInputChange}
              required
              disabled={!formData.kunde_id}
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 ${!formData.kunde_id ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            >
              <option value="">Bitte wählen...</option>
              {filteredStandorte.map((standort) => (
                <option key={standort.id} value={standort.id}>
                  {standort.name}{standort.standortnummer ? ` (${standort.standortnummer})` : ''}
                </option>
              ))}
            </select>
            {!formData.kunde_id && (
              <p className="mt-1 text-xs text-gray-500">
                Bitte wählen Sie zuerst einen Kunden aus
              </p>
            )}
            {formData.kunde_id && filteredStandorte.length === 0 && (
              <p className="mt-1 text-xs text-red-500">
                Keine Standorte für diesen Kunden vorhanden
              </p>
            )}
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">Auftragsbezeichnung</label>
            <textarea
              name="auftragsbezeichnung"
              value={formData.auftragsbezeichnung}
              onChange={handleInputChange}
              required
              rows={4}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 resize-y"
              placeholder="Beschreibung des Auftrags"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Auftragsdatum</label>
            <input
              type="date"
              name="auftragsdatum"
              value={formData.auftragsdatum}
              onChange={handleInputChange}
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">PDF Auftrag</label>
            <input
              type="file"
              ref={fileInputRef}
              accept=".pdf"
              onChange={handleFileChange}
              className="mt-1 block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-md file:border-0
                file:text-sm file:font-semibold
                file:bg-blue-50 file:text-blue-700
                hover:file:bg-blue-100"
            />
            {formData.pdf_name && (
              <p className="mt-1 text-sm text-gray-500">
                Aktuelles PDF: {formData.pdf_name}
              </p>
            )}
          </div>
          
          <div className="md:col-span-2 flex justify-end">
            {editingId && (
              <button
                type="button"
                onClick={cancelEditing}
                className="mr-2 bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 flex items-center gap-2"
              >
                <X size={20} />
                Abbrechen
              </button>
            )}
            <button
              type="submit"
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2 transition-colors"
            >
              {editingId ? (
                <>
                  <Save size={20} />
                  Änderungen speichern
                </>
              ) : (
                <>
                  <Plus size={20} />
                  Auftrag hinzufügen
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-800">Auftragsliste</h2>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Interne Nr.</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Auftragsnummer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monteur</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kunde</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aktionen</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {auftraege.map((auftrag) => {
                const kunde = kunden.find(k => k.id === auftrag.kunde_id);
                return (
                  <tr key={auftrag.id} className={editingId === auftrag.id ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap font-mono text-sm">{auftrag.internal_number}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{auftrag.auftragsnummer}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {monteure.find(m => m.id === auftrag.monteur_id)?.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {kunde?.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        auftrag.status === 'neu' ? 'bg-blue-100 text-blue-800' :
                        auftrag.status === 'begonnen' ? 'bg-yellow-100 text-yellow-800' :
                        auftrag.status === 'abgeschlossen' ? 'bg-green-100 text-green-800' :
                        'bg-purple-100 text-purple-800'
                      }`}>
                        {getStatusLabel(auftrag.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => startEditing(auftrag)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Auftrag bearbeiten"
                        >
                          <Edit size={20} />
                        </button>
                        <button
                          onClick={() => deleteAuftrag(auftrag.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Auftrag löschen"
                        >
                          <Trash2 size={20} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          
          {auftraege.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              Noch keine Aufträge vorhanden
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AuftragsListe;